import type { AdminEventRegistration } from '@mds/shared/types/event-registration';

import { eventPriceLabel } from '@/domain/events';
import { answerLine, registrationFacts, registrationTone } from '@/domain/registrations';
import { download, stamp } from '@/lib/export';

/**
 * One registration as an Excel workbook: the same lines its detail dialog
 * shows, then the guest's answers to the registration form.
 *
 * Built in the browser, so `exceljs` is loaded only when someone exports and
 * never weighs on the admin bundle.
 */

/* The brand palette as ARGB, traceable to `frontend/shared/src/styles/primitives.css`
 * and matching the feedback workbook (`events/feedback-export.ts`).
 * Excel has no tokens, so the literals live here, named for the primitive they copy. */
const MINT_DARK = 'FF168B70'; // --mint-700
const INK_SECONDARY = 'FF5B6472'; // --neutral-500
const HAIRLINE = 'FFDDE3EA'; // --line-100
const WHITE = 'FFFFFFFF';

export async function exportRegistrationXlsx(r: AdminEventRegistration, eventTitle: string) {
  const { default: ExcelJS } = await import('exceljs');

  const book = new ExcelJS.Workbook();
  const sheet = book.addWorksheet(r.reference);
  sheet.columns = [{ width: 28 }, { width: 60 }];

  const title = sheet.getCell('A1');
  title.value = `${r.attendeeName} · ${r.reference}`;
  title.font = { bold: true, size: 16, color: { argb: MINT_DARK } };

  const details = sheet.getCell('A2');
  details.value = `${eventTitle} · exported ${stamp()}`;
  details.font = { color: { argb: INK_SECONDARY } };

  const table = (heading: [string, string], lines: [string, string][]) => {
    sheet.addRow([]);
    const header = sheet.addRow(heading);
    header.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: WHITE } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MINT_DARK } };
      cell.alignment = { vertical: 'middle' };
    });
    header.height = 24;
    for (const line of lines) {
      const row = sheet.addRow(line);
      row.alignment = { vertical: 'top', wrapText: true };
      row.getCell(1).font = { color: { argb: INK_SECONDARY } };
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = { bottom: { style: 'thin', color: { argb: HAIRLINE } } };
      });
    }
  };

  table(
    ['Registration', ''],
    [
      ['Name', r.attendeeName],
      ['Reference', r.reference],
      ['Ticket', r.ticket?.code ?? '—'],
      ['Status', registrationTone(r).label],
      ['Seats', String(r.seatCount)],
      ['Amount', eventPriceLabel(r.price.totalAmountMinor)],
      ...registrationFacts(r).map((f): [string, string] => [f.label, f.value]),
    ],
  );

  if (r.answers.length > 0) {
    table(
      ['Question', 'Answer'],
      r.answers.map(answerLine).map((a): [string, string] => [a.question, a.answer]),
    );
  }

  download(
    `${r.reference}-${stamp()}.xlsx`,
    await book.xlsx.writeBuffer(),
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}
